import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      const response = await axios.get(`${API_URL}/api/auth/me`, config);

      setUser(response.data);
    } catch (error) {
      setUser(false);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const clearLocalSession = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setUser(false);
  };

  // ✅ LOGIN
  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
    });

    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
    }

    await checkAuth();
    return response.data;
  };

  // ✅ REGISTER (FIXED)
  const register = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);

    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
    }
    await checkAuth();
    return response.data;
  };

  // ✅ LOGOUT
  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }

    clearLocalSession();
  };

  const clearSessionForVerify = useCallback(() => {
    clearLocalSession();
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    clearSessionForVerify,
    isAuthenticated: !!user && user !== false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
